/**
 * Round 12 #770 — the L0 bar's "Find anything…": ask the question, the model narrows (Greg). The questions
 * are the interrogative rows of GET /api/data/model; the results are the records of the entity types that
 * answer the chosen question (GET /api/entities per type, under the reader's read permission), grouped by
 * interrogative. Nothing here names a question or a type.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/api/client';
import { Modal, Search } from '@/kit';
import { type DataModel, useDataModel } from '@/features/data/api';
import { QuestionChips } from '@/features/data/ModelPage';
import { entityHref } from '@/lib/entity-links';

/** Dispatch on window to open Find anything (the L0 bar's search box). */
export const OPEN_FIND_EVENT = 'core:open-find';
const MAX_TYPES = 10;

export type PlannedType = { code: string; label: string; interrogative: string; question: string };

/** The types to search: those under the chosen question (or every question), with records, the most populated first. Pure. */
export function searchPlan(model: DataModel, question: string | null): PlannedType[] {
  const inters = model.interrogatives.filter((i) => !question || i.code === question);
  const types = inters.flatMap((i) => i.primitives.flatMap((p) => p.entityTypes.filter((t) => t.recordCount > 0).map((t) => ({ code: t.code, label: t.label, interrogative: i.code, question: i.label, n: t.recordCount }))));
  return types
    .sort((a, b) => b.n - a.n)
    .slice(0, MAX_TYPES)
    .map(({ n: _n, ...t }) => t);
}

export type Hit = { id: string; key: string; name: string };
export type Group = { interrogative: string; question: string; types: { code: string; label: string; hits: Hit[] }[] };

/** The hits grouped by interrogative in the model's order, empty types and questions dropped. Pure. */
export function groupHits(model: DataModel, plan: readonly PlannedType[], hits: Readonly<Record<string, Hit[] | undefined>>): Group[] {
  return model.interrogatives
    .map((i) => ({
      interrogative: i.code,
      question: i.label,
      types: plan.filter((t) => t.interrogative === i.code).map((t) => ({ code: t.code, label: t.label, hits: hits[t.code] ?? [] })).filter((t) => t.hits.length > 0),
    }))
    .filter((g) => g.types.length > 0);
}

export function FindResults({ groups, onPick }: { groups: readonly Group[]; onPick?: () => void }) {
  return (
    <div className="space-y-3" data-testid="find-results">
      {groups.map((g) => (
        <section key={g.interrogative} aria-label={g.question} data-interrogative={g.interrogative}>
          <h3 className="core-section-label m-0 border-0 pt-1">{g.question}</h3>
          {g.types.map((t) => (
            <div key={t.code} className="mb-2">
              <p className="text-[length:var(--t-th)] text-ink-2">{t.label}</p>
              <ul className="core-list m-0 list-none p-0">
                {t.hits.map((h) => {
                  const href = entityHref({ type: t.code, id: h.id, key: h.key });
                  const body = (
                    <>
                      <span className="core-key mr-2">{h.key}</span>
                      {h.name}
                    </>
                  );
                  return (
                    <li key={h.id} data-testid="find-hit">
                      {href ? (
                        <Link to={href} onClick={onPick} className="text-foreground no-underline hover:underline">
                          {body}
                        </Link>
                      ) : (
                        body
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

export function FindAnything() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [question, setQuestion] = useState<string | null>(null);
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_FIND_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_FIND_EVENT, onOpen);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setTerm(text.trim()), 250);
    return () => clearTimeout(t);
  }, [text]);
  const model = useDataModel();
  const plan = useMemo(() => (model.data ? searchPlan(model.data, question) : []), [model.data, question]);
  const results = useQueries({
    queries: plan.map((t) => ({
      queryKey: ['cigs', 'find', t.code, term],
      enabled: open && term.length >= 2,
      staleTime: 30_000,
      retry: false,
      queryFn: async () => ((await apiFetch<{ rows?: Hit[] }>(`/entities?type=${encodeURIComponent(t.code)}&q=${encodeURIComponent(term)}&pageSize=5`)).rows ?? []).map((r) => ({ id: r.id, key: r.key, name: r.name })),
    })),
  });
  const hits = Object.fromEntries(plan.map((t, i) => [t.code, results[i]?.data]));
  const groups = model.data ? groupHits(model.data, plan, hits) : [];
  const busy = results.some((r) => r.isFetching);
  return (
    <Modal open={open} onClose={() => setOpen(false)} size="medium" title="Find anything">
      <div className="flex flex-col gap-3">
        <Search autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Find anything…" aria-label="Find anything" />
        {model.data ? <QuestionChips model={model.data} question={question} onQuestion={setQuestion} /> : null}
        <div className="max-h-[55vh] overflow-y-auto">
          {term.length < 2 ? (
            <p className="text-[length:var(--t-body)] text-muted-foreground">Ask a question, then type a name or a key: the records that answer it appear here, grouped by the question they answer.</p>
          ) : groups.length ? (
            <FindResults groups={groups} onPick={() => setOpen(false)} />
          ) : busy ? (
            <p className="text-[length:var(--t-body)] text-muted-foreground">Searching…</p>
          ) : (
            <p className="text-[length:var(--t-body)] text-muted-foreground">Nothing you can open matches “{term}”.</p>
          )}
        </div>
      </div>
    </Modal>
  );
}
